import { useFormik } from 'formik';
import { Link } from 'gatsby';
import React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Button, Col, Container, Form, InputGroup, Row } from 'react-bootstrap';
import ElementAlert from 'src/components/ElementAlert/ElementAlert';
import useWebFormAwsVoucher from './useWebFormAwsVoucher';
import useReCaptcha from 'src/hooks/useReCaptcha';
import useTheme from 'src/hooks/useTheme';
import getCookie from 'src/utils/get-cookie';

function WebFormAwsVoucher() {
  const formStepContainer = useRef(null);
  const t = useTheme();
  const [pageStep, setPageStep] = useState('showForm');
  const formSender = useWebFormAwsVoucher({ setPageStep });
  const reCaptcha = useReCaptcha('awsVoucherSubmit');

  useEffect(() => {
    if (pageStep !== 'showForm') {
      formStepContainer.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [pageStep]);

  const initialValues = {
    name: '',
    email: '',
    message: '',
    phone: '',
    companyName: '',
    awsAccountId: '',
    gdpr: false,
    hubspotutk: '',
  };

  const onSubmit = async (formData) => {
    const success = await reCaptcha.verify(formData);
    if (!success) {
      return setPageStep('fatalError');
    }

    const data = {
      form: {
        ...formik.values,
        hubspotutk: getCookie('hubspotutk'),
      },
    };

    formSender.send(data);
  };

  const formik = useFormik({ initialValues, onSubmit });

  const formContainer = (
    <Container className="">
      <h5 className="text-center mb-4 text-gray">REGISZTRÁCIÓ</h5>
      <h3 className="h3 text-center mb-4 text-white mb-6">Töltsd ki az alábbi űrlapot, és küldjük a vouchert! Amit kapsz:</h3>
      <Row className="mb-6 mt-8 d-flex">
        <Col xs={12} md={4}>
          <div className="text-center mb-4">
            <img width={100} height={100} src="/images/card-and-cart.webp" alt="AWS Voucher Icon" className="icon" />
            <p className="text-center text-white my-4">Egyedi AWS voucher</p>
          </div>
        </Col>

        <Col xs={12} md={4}>
          <div className="text-center mb-4">
            <img width={100} height={100} src="/images/men-and-headphone.webp" alt="AWS Voucher Icon" className="icon" />
            <p className="text-center text-white my-4">Lehetőség saját projekteken kipróbálni az AWS-t</p>
          </div>
        </Col>

        <Col xs={12} md={4}>
          <div className="text-center mb-4">
            <img width={100} height={100} src="/images/mobile-and-chat.webp" alt="AWS Voucher Icon" className="icon" />
            <p className="text-center text-white my-4">Igény esetén technikai és üzleti támogatás</p>
          </div>
        </Col>
      </Row>
      <Row>
        <Col xs={12} md={{ span: 8, offset: 2 }} xl={{ span: 6, offset: 3 }}>
          <Form onSubmit={formik.handleSubmit}>
            {/* Name */}
            <Form.Group controlId="name" className="mb-4">
              <Form.Label>Név *</Form.Label>
              <Form.Control required type="text" placeholder="Név" name="name" value={formik.values.name} onChange={formik.handleChange} />
            </Form.Group>

            {/* Email */}
            <Form.Group controlId="email" className="mb-4">
              <Form.Label>E-mail *</Form.Label>
              <Form.Control
                required
                type="text"
                placeholder="E-mail"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
              />
            </Form.Group>

            {/* Phone */}
            <Form.Group controlId="phone" className="mb-4">
              <Form.Label>Mobiltelefonszám *</Form.Label>
              <InputGroup>
                <InputGroup.Text style={{ borderRadius: `.25rem 0 0 .25rem` }}>+36</InputGroup.Text>
                <Form.Control
                  style={{ borderRadius: `0 .25rem .25rem 0` }}
                  required
                  type="tel"
                  placeholder="00 000 0000"
                  name="phone"
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                  pattern="^(?:20|30|50|70)\s?(?:\d\s?){6}\d$"
                />
              </InputGroup>
              <Form.Text muted>A formátum a következő: +36 00 000 0000.</Form.Text>
            </Form.Group>

            {/* Company Name */}
            <Form.Group controlId="companyName" className="mb-4">
              <Form.Label>Cégnév</Form.Label>
              <Form.Control
                type="text"
                placeholder="Cégnév"
                name="companyName"
                value={formik.values.companyName}
                onChange={formik.handleChange}
              />
            </Form.Group>

            {/* Name */}
            <Form.Group controlId="name" className="mb-4">
              <Form.Label>AWS account ID (Opcionális)</Form.Label>
              <Form.Control
                type="text"
                placeholder="AWS account ID"
                name="awsAccountId"
                value={formik.values.awsAccountId}
                onChange={formik.handleChange}
              />
            </Form.Group>

            {/* Message */}
            <Form.Group controlId="message" className="mb-4">
              <Form.Label>Megjegyzés:</Form.Label>
              <Form.Control
                type="text"
                placeholder="Mire használnád a vouchert? Mi érdekel leginkább az AWS-ben?"
                name="message"
                value={formik.values.message}
                onChange={formik.handleChange}
                as="textarea"
                maxLength={255}
                rows={4}
              />
            </Form.Group>

            {/* GDPR */}
            <Form.Group controlId="gdpr" className="mb-4">
              <div className="custom-control custom-checkbox">
                <input
                  className="custom-control-input"
                  type="checkbox"
                  id="gdpr"
                  name="gdpr"
                  checked={formik.values.gdpr}
                  onChange={formik.handleChange}
                  required
                />
                <label className="custom-control-label" htmlFor="gdpr" aria-label="Consent to management of my data">
                  <span className="text-size-sm text-white">
                    <span className="mr-1">{t.form.gdprConsent}</span>
                    <Link to={`/${t.navigation.gdpr.path}`} className="text-secondary font-weight-semi-bold">
                      {t.navigation.gdpr.label}
                    </Link>
                    <span className="ml-1">*</span>
                  </span>
                </label>
              </div>
            </Form.Group>

            <div className="d-flex justify-content-center mt-8">
              <Button variant="gradient-secondary" type="submit" disabled={formik.isSubmitting}>
                Regisztrálok
                {formik.isSubmitting && <span> ...</span>}
              </Button>
            </div>
          </Form>

          {formik.error && (
            <ElementAlert
              variant="danger"
              title={t.form.confirmation.errorTitle}
              description={t.form.confirmation.errorDescription}
              className="animate-up mt-6"
            />
          )}
        </Col>
      </Row>
    </Container>
  );

  const fatalError = (
    <Container className="pt-8">
      <Row>
        <Col xs={12} md={{ span: 8, offset: 2 }} lg={{ span: 6, offset: 3 }}>
          <Col className="mb-6">
            <h2 className="text-size-lg text-uppercase text-center text-white">Rendszerhiba</h2>
            <p className="text-center text-white">
              Sajnos nem sikerült elfogadnunk az űrlapodat.
              <br />
              Kérjük vedd fel velünk a kapcsolatot az&nbsp;
              <a className="font-weight-bold text-secondary" href="mailto:aws@cheppers.com">
                aws@cheppers.com
              </a>
              &nbsp;e-mail címen keresztül &quot;AWS Voucher&quot; tárggyal.
            </p>
            <p className="pb-5 text-center text-white">Köszönjük!</p>
          </Col>
        </Col>
      </Row>
    </Container>
  );

  const success = (
    <Container className="pt-8">
      <Row>
        <Col xs={12} md={{ span: 8, offset: 2 }} lg={{ span: 6, offset: 3 }}>
          <Col className="mb-6">
            <h5 className="text-center mb-4 text-gray">REGISZTRÁCIÓ</h5>
            <h3 className="h3 text-center mb-4 text-white mb-6">Köszönjük a regisztrációdat!</h3>
            <p className="text-white">
              Sikeresen megkaptuk az adataidat – hamarosan küldjük az AWS vouchedredet az általad megadott e-mail címre. Ha pár percen belül
              nem érkezik meg az e-mail, ellenőrizd a spam/promóciók mappádat is – és ha bármi gond lenne, keress minket bátran.
              <br />
              <br />
              Ha időközben bármilyen kérdésed lenne, vagy szívesen beszélgetnél az AWS lehetőségeiről, szívesen segítünk – csak jelezd
              nekünk!
            </p>
            <div className="d-flex justify-content-center mt-8">
              <Link to="/kapcsolat" className="btn btn-link-secondary">
                Kapcsolat
              </Link>
            </div>
          </Col>
        </Col>
      </Row>
    </Container>
  );

  const currentStep = () => {
    switch (pageStep) {
      case 'showForm':
        return formContainer;
      case 'fatalError':
        return fatalError;
      case 'success':
        return success;
      default:
        throw new Error('Should have returend one of the steps.');
    }
  };

  return (
    <Container ref={formStepContainer} fluid className="bg-gray-800 px-0 py-8">
      {currentStep()}
    </Container>
  );
}

export default WebFormAwsVoucher;
